import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getToken } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { prefetchHomeData } from "@/lib/prefetch";
import { SplashScreen } from "./SplashScreen";

// Minimum splash visibility so the transition never feels like a flicker,
// and a hard cap so a slow network can't trap the user on the splash.
const MIN_SPLASH_MS = 450;
const MAX_SPLASH_MS = 2500;

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hasToken = !!getToken();
  const { data: user, isLoading, isError } = useMe();
  const qc = useQueryClient();
  const [prefetchDone, setPrefetchDone] = useState(false);
  const minDelayElapsed = useMinDelay(MIN_SPLASH_MS);

  // Warm the Home-page query cache while the splash is up, so by the time we
  // unmount the splash, Home data is already there — no skeleton flash.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setPrefetchDone(true);
    }, MAX_SPLASH_MS);
    prefetchHomeData(qc).finally(() => {
      if (cancelled) return;
      clearTimeout(timeout);
      setPrefetchDone(true);
    });
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [user, qc]);

  if (!hasToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (isLoading || !minDelayElapsed || !prefetchDone) {
    return <SplashScreen />;
  }

  if (isError || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

function useMinDelay(ms: number): boolean {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setDone(true), ms);
    return () => clearTimeout(id);
  }, [ms]);
  return done;
}
